import React, { useEffect, useState } from 'react'
import { ExpandMore } from '@mui/icons-material';
import DescriptionIcon from '@mui/icons-material/Description';
import GetAppIcon from '@mui/icons-material/GetApp';
import { IconButton } from '@mui/material';
import { makeStyles } from '@mui/styles';

//Components
import ResultNotFoundComponent from '@/main/components/shared/resultNotFound/resultNotFoundComponent';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import { SpinnerLoading } from '@/main/components/shared/styles';
import TooltipComponent from '@/main/components/shared/tooltip/tooltipComponent';
import { editSquareSVG } from '@/main/components/shared/icons/svg/editSquareSVG';

//Services
import { getTranslateByKey, isNullUndefined } from '@/main/services/utils';
import { userIsSupplier } from '@/main/services/utilsUser';
import { downloadFile } from '@/main/services/utilsFile';
import { formatDate, getPatternDate } from '@/main/services/utilsDate';

//Models
import { InvoiceFileDataResponse, InvoiceResponse } from '@/main/models/response';

//Api
import { invoiceApi } from '@/main/api';

// Styles and local components
import *  as S from './styles';


const Invoice = ({ invoice }: { invoice: InvoiceFileDataResponse }) => {
  const [isLoadingDownloadFile, setIsLoadingDownloadFile] = useState(false);

  function handleDownloadFile(guidInvoiceFile: string) {
    setIsLoadingDownloadFile(true);
    invoiceApi
      .getfilebyguidinvoicefile(guidInvoiceFile)
      .then((response) => {
        if (response.data.result) {
          const base64File = response.data.result;
          const nameFile = `${base64File.fileName}.${base64File.fileExtension.toLowerCase()}`
          downloadFile(base64File.file, nameFile);
        }
      })
      .finally(() => {
        setIsLoadingDownloadFile(false);
      })
  }

  const nameFileText = `${invoice.fileName}.${invoice.fileExtension}`;

  return (
    <S.Invoice >
      <DescriptionIcon fontSize='medium' />
      <p>{nameFileText}</p>
      <S.ButtonDownloadFile
        id={`id-modalInvoice-btn-downloadFile-${nameFileText}`}
        disabled={isLoadingDownloadFile}
        onClick={() => handleDownloadFile(invoice.guidInvoiceFile)}
      >
        {isLoadingDownloadFile ? <SpinnerLoading /> : <GetAppIcon fontSize='medium' />}
      </S.ButtonDownloadFile>
    </S.Invoice>
  )
}

interface IInvoiceProps {
  invoiceBySupplier: InvoiceResponse;
  handleEdit: (
    isEdit: boolean,
    guid: string,
    invoiceNumber: string,
    issueDate: Date,
    supplierName: string,
    invoicesFile: InvoiceFileDataResponse[],
    creationDate: Date
  ) => void
}
const SupplierInvoice = ({
  invoiceBySupplier,
  handleEdit
}: IInvoiceProps) => {

  const useStylesTooltip = makeStyles({
    popperCustom: { marginLeft: 15 }
  });

  const classesTooltip = useStylesTooltip();

  const isUserSupplier = userIsSupplier();

  if (!invoiceBySupplier) return <ResultNotFoundComponent />
  const isBuyer = !isUserSupplier;

  return (
    <S.AccordionSupplier defaultExpanded>
      {isBuyer && (
        <S.AccordionSummarySupplier
          id="id-modalInvoice-accordion-invoice"
          aria-controls="categories-content"
          expandIcon={<ExpandMore fontSize='medium' />}
        >
          <S.AccordionTitle title={invoiceBySupplier.supplierName}>{invoiceBySupplier.supplierName}</S.AccordionTitle>
        </S.AccordionSummarySupplier>
      )}
      {invoiceBySupplier.invoicesSupplier.map((invoiceSupplier) => (
        <S.AccordionDetailsSupplier>
          <S.BoxLabel>
            <S.ContainerLabel>
              <S.LabelText>
                {getTranslateByKey('information.invoiceNumber')}:
              </S.LabelText>
              {invoiceSupplier.invoiceNumber}
            </S.ContainerLabel>

            <S.ContainerLabel>
              <S.LabelText>
                {getTranslateByKey('information.issueDate')}:
              </S.LabelText>
              {formatDate(invoiceSupplier.issueDate, getPatternDate())}
            </S.ContainerLabel>
            {!isNullUndefined(invoiceSupplier.canUserVisualizeEditInvoice) && invoiceSupplier.canUserVisualizeEditInvoice && (
              <TooltipComponent
                title={getTranslateByKey('control.editInvoice')}
                stylePopper={classesTooltip.popperCustom}>
                <S.ButtonEdit>
                  <IconButton onClick={() => {
                    handleEdit(
                      true,
                      invoiceSupplier.guidInvoice,
                      invoiceSupplier.invoiceNumber,
                      invoiceSupplier.issueDate,
                      invoiceBySupplier.supplierName,
                      invoiceSupplier.invoices,
                      invoiceSupplier.creationDate
                    )
                  }}>
                    {editSquareSVG()}
                  </IconButton>
                </S.ButtonEdit>
              </TooltipComponent>
            )}
          </S.BoxLabel >
          {invoiceSupplier.invoices.length > 0 ?
            invoiceSupplier.invoices.map((invoice) => (
              <Invoice
                key={invoiceSupplier.guidInvoice}
                invoice={invoice}
              />
            )) :
            <ResultNotFoundComponent />
          }
        </S.AccordionDetailsSupplier >
      ))}

    </S.AccordionSupplier >
  )
}

interface IModalInvoiceQuoteProps {
  isEdit: boolean;
  guidsProcurementSupplierItem: string[];
  handleModalInvoiceQuote: () => void;
  handleEdit: (
    isEdit: boolean,
    guid: string,
    invoiceNumber: string,
    issueDate: Date,
    supplierName: string,
    invoicesFile: InvoiceFileDataResponse[],
    creationDate: Date
  ) => void
}

function ModalInvoiceQuote({
  isEdit,
  guidsProcurementSupplierItem,
  handleModalInvoiceQuote,
  handleEdit
}: IModalInvoiceQuoteProps) {

  //#region states
  const [dataInvoices, setDataInvoices] = React.useState<InvoiceResponse[]>([]);
  const [isLoadingData, setIsLoadingData] = React.useState(false);
  //#endregion;


  function loadInvoices() {
    setIsLoadingData(true);
    invoiceApi
      .get(guidsProcurementSupplierItem)
      .then((response) => {
        setDataInvoices(response.data.result)
      })
      .finally(() => {
        setIsLoadingData(false);
      })
  }


  //#region useEffect
  useEffect(() => {
    if (guidsProcurementSupplierItem && guidsProcurementSupplierItem.length > 0) {
      loadInvoices()
    }
  }, [JSON.stringify(guidsProcurementSupplierItem)])

  useEffect(() => {
    if (!isEdit) {
      loadInvoices()
    }
  }, [isEdit])
  //#endregion;

  return (
    <S.Container>
      {isLoadingData ?
        <SpinnerLoading style={{ margin: "0 auto 1rem" }} /> :
        dataInvoices.map((invoice) => (
          <>
            <SupplierInvoice
              key={invoice.guidSupplier}
              invoiceBySupplier={invoice}
              handleEdit={handleEdit}
            />
          </>
        ))
      }

      <SecondaryButton
        id='id-modalInvoice-btn-back'
        onClick={handleModalInvoiceQuote}
      >
        {getTranslateByKey('information.back')}
      </SecondaryButton>
    </S.Container>
  )
}

export default ModalInvoiceQuote